import React, { useEffect, useState } from 'react';
import { playersAPI, weightAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CalendarIcon, Scale, TrendingUp, TrendingDown, Minus, Target, Calendar as CalendarWeek, Users } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

export default function WeightPage() {
  const [players, setPlayers] = useState([]);
  const [weightLogs, setWeightLogs] = useState({});
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [positionData, setPositionData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [inputValues, setInputValues] = useState({});
  const [viewMode, setViewMode] = useState('daily'); // 'daily', 'weekly', 'monthly', 'position'

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  // Calculate average weight by position
  const getAvgWeightByPosition = () => {
    const positionGroups = {};
    players.forEach(player => {
      const pos = (player.position || 'Unknown').toUpperCase();
      if (!positionGroups[pos]) {
        positionGroups[pos] = { total: 0, count: 0 };
      }
      if (player.current_weight) {
        positionGroups[pos].total += player.current_weight;
        positionGroups[pos].count += 1;
      }
    });
    
    // Position initials mapping
    const positionMap = {
      "QUARTERBACK": "QB", "WIDE RECEIVER": "WR", "RUNNING BACK": "RB",
      "TIGHT END": "TE", "LINEBACKER": "LB", "CORNERBACK": "CB",
      "SAFETY": "S", "KICKER": "K", "PUNTER": "P", "CENTER": "C",
      "GUARD": "G", "TACKLE": "T", "FULLBACK": "FB", "DEFENSIVE END": "DE",
      "DEFENSIVE TACKLE": "DT", "OFFENSIVE LINE": "OL", "DEFENSIVE LINE": "DL",
    };
    
    const getInitials = (pos) => {
      if (positionMap[pos]) return positionMap[pos];
      const words = pos.split(' ');
      return words.length === 1 ? pos.slice(0, 2) : words.map(w => w[0]).join('');
    };
    
    return Object.entries(positionGroups)
      .filter(([_, data]) => data.count > 0)
      .map(([position, data]) => ({
        position,
        initials: getInitials(position),
        avgWeight: Math.round(data.total / data.count)
      }));
  };

  useEffect(() => {
    loadData();
  }, [dateStr, viewMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      const playersRes = await playersAPI.getAll();
      setPlayers(playersRes.data);

      if (viewMode === 'daily') {
        const weightRes = await weightAPI.getAll(dateStr);
        const logsMap = {};
        const inputsMap = {};
        weightRes.data.forEach(log => {
          logsMap[log.player_id] = log;
          inputsMap[log.player_id] = log.weight.toString();
        });
        setWeightLogs(logsMap);
        setInputValues(inputsMap);
      } else if (viewMode === 'weekly') {
        const weeklyRes = await weightAPI.getWeekly();
        setWeeklyData(weeklyRes.data);
      } else if (viewMode === 'monthly') {
        const monthlyRes = await weightAPI.getMonthly();
        setMonthlyData(monthlyRes.data);
      } else if (viewMode === 'position') {
        const positionRes = await weightAPI.getByPosition();
        setPositionData(positionRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleWeightChange = (playerId, value) => {
    setInputValues(prev => ({ ...prev, [playerId]: value }));
  };

  const handleWeightSubmit = async (playerId) => {
    const weight = parseFloat(inputValues[playerId]);
    if (isNaN(weight) || weight <= 0) {
      toast.error('Please enter a valid weight');
      return;
    }

    try {
      const res = await weightAPI.log({
        player_id: playerId,
        weight,
        date: dateStr
      });
      
      setWeightLogs(prev => ({ ...prev, [playerId]: res.data }));
      toast.success('Weight logged');
      
      const playersRes = await playersAPI.getAll();
      setPlayers(playersRes.data);
    } catch (error) {
      toast.error('Failed to log weight');
    }
  };

  const handleKeyDown = (e, playerId) => {
    if (e.key === 'Enter') {
      handleWeightSubmit(playerId);
    }
  };

  const getDeltaDisplay = (log) => {
    if (!log?.delta) return null;
    const delta = log.delta;
    if (delta > 0) {
      return (
        <span className="flex items-center gap-1 text-[#32D74B]">
          <TrendingUp className="w-4 h-4" />
          +{delta} lbs
        </span>
      );
    } else if (delta < 0) {
      return (
        <span className="flex items-center gap-1 text-[#FF3B30]">
          <TrendingDown className="w-4 h-4" />
          {delta} lbs
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-[#71717A]">
        <Minus className="w-4 h-4" />
        No change
      </span>
    );
  };

  const getTargetStatus = (player) => {
    if (!player.current_weight || !player.target_weight) return null;
    const diff = player.current_weight - player.target_weight;
    if (Math.abs(diff) <= 2) {
      return <span className="text-[#32D74B] text-xs">On Target</span>;
    } else if (diff > 0) {
      return <span className="text-[#FFD60A] text-xs">+{diff.toFixed(1)} over</span>;
    } else {
      return <span className="text-[#007AFF] text-xs">{Math.abs(diff).toFixed(1)} under</span>;
    }
  };

  const stats = {
    logged: Object.keys(weightLogs).length,
    total: players.length,
    onTarget: players.filter(p => {
      if (!p.current_weight || !p.target_weight) return false;
      return Math.abs(p.current_weight - p.target_weight) <= 2;
    }).length
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="overline">Performance Tracking</p>
            <h1 className="font-display text-4xl lg:text-5xl text-white mt-2">WEIGHT</h1>
          </div>

          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
              <TabsList className="bg-[#121212] border border-[#27272A]">
                <TabsTrigger 
                  value="daily" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="daily-weight-tab"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Daily
                </TabsTrigger>
                <TabsTrigger 
                  value="weekly" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="weekly-weight-tab"
                >
                  <CalendarWeek className="w-4 h-4 mr-2" />
                  Weekly
                </TabsTrigger>
                <TabsTrigger 
                  value="monthly" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="monthly-weight-tab"
                >
                  <CalendarWeek className="w-4 h-4 mr-2" />
                  Monthly
                </TabsTrigger>
                <TabsTrigger 
                  value="position" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="position-weight-tab"
                >
                  <Users className="w-4 h-4 mr-2" />
                  By Position
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {viewMode === 'daily' && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-[#27272A] bg-[#121212] text-white hover:bg-[#1A1A1A]"
                    data-testid="date-picker-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(selectedDate, 'MMM d, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121212] border-[#27272A]" align="end">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="bg-[#121212]"
                    data-testid="weight-calendar"
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Stats Row - Daily View Only */}
        {viewMode === 'daily' && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-[#121212] border-[#27272A]" data-testid="stat-logged">
              <CardContent className="p-4 text-center">
                <p className="font-display text-3xl text-white">{stats.logged}/{stats.total}</p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Logged Today</p>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-[#27272A]" data-testid="stat-on-target">
              <CardContent className="p-4 text-center">
                <p className="font-display text-3xl text-[#32D74B]">{stats.onTarget}</p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">On Target</p>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-[#27272A]" data-testid="stat-completion">
              <CardContent className="p-4 text-center">
                <p className="font-display text-3xl text-white">
                  {stats.total > 0 ? Math.round((stats.logged / stats.total) * 100) : 0}%
                </p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Completion</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Avg Weight by Position - All Views except Position */}
        {viewMode !== 'position' && !loading && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[#71717A] uppercase tracking-wider">Avg by Position:</span>
            {getAvgWeightByPosition().map((pos) => (
              <div key={pos.position} className="flex items-center gap-1 px-2 py-1 bg-[#1A1A1A] border border-[#27272A]">
                <span className="text-[#FF3B30] font-bold text-sm">{pos.initials}</span>
                <span className="text-white text-sm">{pos.avgWeight}</span>
              </div>
            ))}
            {getAvgWeightByPosition().length === 0 && (
              <span className="text-[#71717A] text-sm">No weight data</span>
            )}
          </div>
        )}

        {/* Content based on view mode */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-[#71717A]">Loading...</div>
          </div>
        ) : viewMode === 'daily' ? (
          /* Daily View - Weight Entry */
          <Card className="bg-[#121212] border-[#27272A]" data-testid="weight-entry-list">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center gap-2 text-white">
                <Scale className="w-5 h-5" />
                Log Weight - {format(selectedDate, 'EEEE, MMMM d')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {players.length > 0 ? (
                <div className="divide-y divide-[#27272A]">
                  {players.map((player) => (
                    <div 
                      key={player.id}
                      className="flex items-center justify-between p-4 hover:bg-[#1A1A1A] transition-colors"
                      data-testid={`weight-row-${player.id}`}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 bg-[#27272A] flex items-center justify-center text-white font-bold">
                          #{player.jersey_number}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium">
                            {player.name}
                            {player.locker_number && (
                              <span className="ml-2 text-[#FF3B30] text-sm">(Loop #{player.locker_number})</span>
                            )}
                          </p>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-[#71717A]">{player.position}</span>
                            {player.target_weight && (
                              <span className="flex items-center gap-1 text-[#71717A]">
                                <Target className="w-3 h-3" />
                                {player.target_weight} lbs
                              </span>
                            )}
                            {getTargetStatus(player)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="hidden sm:block w-24 text-right text-sm">
                          {getDeltaDisplay(weightLogs[player.id])}
                        </div>

                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={inputValues[player.id] || ''}
                            onChange={(e) => handleWeightChange(player.id, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, player.id)}
                            placeholder={player.current_weight?.toString() || '---'}
                            className="weight-input w-24 bg-[#0A0A0A] border-[#27272A] text-white text-center"
                            data-testid={`weight-input-${player.id}`}
                          />
                          <span className="text-[#71717A] text-sm">lbs</span>
                        </div>

                        <Button
                          onClick={() => handleWeightSubmit(player.id)}
                          disabled={!inputValues[player.id]}
                          className={cn(
                            "bg-[#FF3B30] hover:bg-[#D32F2F] text-white btn-press",
                            !inputValues[player.id] && "opacity-50"
                          )}
                          data-testid={`save-weight-${player.id}`}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <Scale className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No players in roster</p>
                  <p className="text-sm">Add players to track weight</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'weekly' ? (
          /* Weekly View */
          <Card className="bg-[#121212] border-[#27272A]" data-testid="weekly-weight">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center gap-2 text-white">
                <CalendarWeek className="w-5 h-5" />
                Weekly Overview - Current Week (Mon-Fri)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Player Grid */}
              {weeklyData?.players && weeklyData.players.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold">Player</th>
                        {weeklyData.summary?.map((day, idx) => (
                          <th key={idx} className="p-4 text-center text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">
                            <div>{day.day}</div>
                            <div className="text-[10px] font-normal">{day.date ? format(new Date(day.date), 'MM/dd') : ''}</div>
                          </th>
                        ))}
                        <th className="p-4 text-center text-xs text-[#71717A] uppercase tracking-wider font-bold w-24">
                          Weekly Δ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {weeklyData.players.map((player) => (
                        <tr key={player.player_id} className="border-b border-[#27272A] hover:bg-[#1A1A1A]">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <span className="text-[#FF3B30] font-bold">#{player.jersey_number}</span>
                              <div>
                                <span className="text-white">
                                  {player.name}
                                  {player.locker_number && (
                                    <span className="ml-2 text-[#71717A] text-sm">(Loop #{player.locker_number})</span>
                                  )}
                                </span>
                                <p className="text-xs text-[#71717A]">{player.position}</p>
                              </div>
                            </div>
                          </td>
                          {player.weights.map((w, idx) => (
                            <td key={idx} className="p-4 text-center">
                              {w.weight ? (
                                <div>
                                  <span className="text-white font-medium">{w.weight}</span>
                                  {w.delta && (
                                    <span className={cn(
                                      "text-xs ml-1",
                                      w.delta > 0 ? "text-[#32D74B]" : w.delta < 0 ? "text-[#FF3B30]" : "text-[#71717A]"
                                    )}>
                                      {w.delta > 0 ? '+' : ''}{w.delta}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[#71717A]">-</span>
                              )}
                            </td>
                          ))}
                          <td className="p-4 text-center">
                            {player.weekly_change !== null ? (
                              <span className={cn(
                                "font-bold text-lg",
                                player.weekly_change > 0 ? "text-[#32D74B]" : player.weekly_change < 0 ? "text-[#FF3B30]" : "text-[#71717A]"
                              )}>
                                {player.weekly_change > 0 ? '+' : ''}{player.weekly_change}
                              </span>
                            ) : (
                              <span className="text-[#71717A]">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <Scale className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No weight data</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'monthly' ? (
          /* Monthly View */
          <Card className="bg-[#121212] border-[#27272A]" data-testid="monthly-weight">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center gap-2 text-white">
                <CalendarWeek className="w-5 h-5" />
                Monthly Overview - Last 30 Days
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Average Weight by Position Summary */}
              {monthlyData?.players && monthlyData.players.length > 0 && (
                <div className="p-6 border-b border-[#27272A]">
                  <p className="text-xs text-[#71717A] uppercase tracking-wider mb-4">Average Weight by Position</p>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {(() => {
                      // Group by position (case-insensitive)
                      const positionGroups = {};
                      monthlyData.players.forEach(player => {
                        const pos = (player.position || 'Unknown').toUpperCase();
                        if (!positionGroups[pos]) {
                          positionGroups[pos] = { total: 0, count: 0 };
                        }
                        if (player.current_weight) {
                          positionGroups[pos].total += player.current_weight;
                          positionGroups[pos].count += 1;
                        }
                      });
                      return Object.entries(positionGroups)
                        .filter(([_, data]) => data.count > 0)
                        .map(([position, data]) => (
                          <div key={position} className="p-3 bg-[#1A1A1A] border border-[#27272A]">
                            <p className="text-xs text-[#71717A]">{position}</p>
                            <p className="font-bold text-xl text-white">
                              {Math.round(data.total / data.count)} lbs
                            </p>
                            <p className="text-xs text-[#71717A]">{data.count} players</p>
                          </div>
                        ));
                    })()}
                  </div>
                </div>
              )}

              {/* Player Grid - Left aligned */}
              {monthlyData?.players && monthlyData.players.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold">Player</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">Start</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">Current</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">Target</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">30-Day Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.players.map((player) => (
                        <tr key={player.player_id} className="border-b border-[#27272A] hover:bg-[#1A1A1A]">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="text-[#FF3B30] font-bold">#{player.jersey_number}</span>
                              <span className="text-white">
                                {player.name}
                                {player.locker_number && (
                                  <span className="ml-2 text-[#71717A] text-sm">(Loop #{player.locker_number})</span>
                                )}
                              </span>
                              <span className="text-xs text-[#71717A]">• {player.position}</span>
                            </div>
                          </td>
                          <td className="p-4 text-left">
                            <span className="text-[#71717A]">{player.start_weight || '-'}</span>
                          </td>
                          <td className="p-4 text-left">
                            <span className="text-white font-medium">{player.current_weight || '-'}</span>
                          </td>
                          <td className="p-4 text-left">
                            <span className="text-[#FFD60A]">{player.target_weight || '-'}</span>
                          </td>
                          <td className="p-4 text-left">
                            {player.monthly_change !== null ? (
                              <span className={cn(
                                "font-bold text-lg",
                                player.monthly_change > 0 ? "text-[#32D74B]" : player.monthly_change < 0 ? "text-[#FF3B30]" : "text-[#71717A]"
                              )}>
                                {player.monthly_change > 0 ? '+' : ''}{player.monthly_change}
                              </span>
                            ) : (
                              <span className="text-[#71717A]">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <Scale className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No weight data</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Position View - Weight by Position */
          <div className="space-y-6" data-testid="position-weight">
            {/* Average Weight by Position Summary */}
            <Card className="bg-[#121212] border-[#27272A]">
              <CardHeader className="border-b border-[#27272A]">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Users className="w-5 h-5 text-[#007AFF]" />
                  Average Weight by Position
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {positionData.length > 0 ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {positionData.map((position) => {
                      // Calculate average weight for this position
                      const playersWithWeight = position.players.filter(p => p.current_weight);
                      const avgWeight = playersWithWeight.length > 0 
                        ? Math.round(playersWithWeight.reduce((sum, p) => sum + p.current_weight, 0) / playersWithWeight.length)
                        : null;
                      
                      return (
                        <div key={position.position} className="p-4 bg-[#1A1A1A] border border-[#27272A]">
                          <p className="text-xs text-[#71717A] uppercase">{position.position}</p>
                          <p className="font-bold text-2xl text-white mt-1">
                            {avgWeight ? `${avgWeight} lbs` : '-'}
                          </p>
                          <p className="text-xs text-[#71717A] mt-1">{position.player_count} players</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8 text-[#71717A]">
                    No position data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Position Details - Left aligned */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {positionData.map((position) => (
                <Card key={position.position} className="bg-[#121212] border-[#27272A]">
                  <CardHeader className="border-b border-[#27272A] py-3">
                    <CardTitle className="flex items-center justify-between text-white text-lg">
                      <span>{position.position}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#71717A]">{position.player_count} players</span>
                        {position.avg_change !== null && (
                          <span className={cn(
                            "px-2 py-1 text-sm font-bold",
                            position.avg_change >= 0 
                              ? "bg-[#32D74B]/10 text-[#32D74B] border border-[#32D74B]/30" 
                              : "bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30"
                          )}>
                            {position.avg_change >= 0 ? '+' : ''}{position.avg_change} lbs avg
                          </span>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-[#27272A]">
                      {position.players.map((player, idx) => (
                        <div key={idx} className="flex items-center p-3 hover:bg-[#1A1A1A]">
                          <div className="flex items-center gap-2 flex-1">
                            <span className="text-[#FF3B30] font-bold text-sm">#{player.jersey_number}</span>
                            <span className="text-white">{player.name}</span>
                            <span className="text-[#71717A] text-sm ml-2">
                              {player.current_weight ? `${player.current_weight} lbs` : '-'}
                            </span>
                            {player.change !== null && (
                              <span className={cn(
                                "font-medium text-sm ml-1",
                                player.change >= 0 ? "text-[#32D74B]" : "text-[#FF3B30]"
                              )}>
                                ({player.change >= 0 ? '+' : ''}{player.change})
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {positionData.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                <Users className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg">No players in roster</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
